import type {
  VisualizationDTO,
  VisualizationInsertDTO,
  VisualizationType,
} from '@tempo-io/roadmap-dto';
import { HTTPError } from 'ky';
import { tempoApi } from '@/common/api/tempoApi';

const fetchAllVisualizations = async (viewId: number): Promise<VisualizationDTO[]> => {
  const response = await tempoApi()
    .get(`api/v1/loop-visualization/view/${viewId}/visualization`)
    .json<VisualizationDTO[]>();
  return response;
};

const fetchVisualization = async (
  viewId: number,
  visualizationId: number,
): Promise<VisualizationDTO> => {
  const response = await tempoApi()
    .get(`api/v1/loop-visualization/view/${viewId}/visualization/${visualizationId}`)
    .json<VisualizationDTO>();
  return response;
};

const fetchVisualizationByType = async (
  viewId: number,
  type: VisualizationType,
): Promise<VisualizationDTO | null> => {
  try {
    const response = await tempoApi()
      .get(`api/v1/loop-visualization/view/${viewId}/visualization/type/${type}`)
      .json<VisualizationDTO | null>();
    return response;
  } catch (err: unknown) {
    if (err instanceof HTTPError && err.response.status === 404) {
      return null;
    }
    throw err;
  }
};

const upsertVisualization = async (
  viewId: number,
  data: VisualizationInsertDTO,
): Promise<VisualizationDTO> => {
  try {
    return await tempoApi()
      .put(`api/v1/loop-visualization/view/${viewId}/visualization`, {
        json: data,
      })
      .json<VisualizationDTO>();
  } catch (error) {
    const isDeterministicClientError =
      error instanceof HTTPError && error.response.status >= 400 && error.response.status < 500;
    if (isDeterministicClientError) {
      throw error;
    }

    // The create endpoint is idempotent, but a retried request can still land after an
    // earlier attempt already succeeded if the response itself was lost (timeout, dropped
    // connection, transient 5xx). Check reality before surfacing an error in that case.
    const existing = await fetchVisualizationByType(viewId, data.type).catch(() => null);
    if (existing) {
      return existing;
    }
    throw error;
  }
};

const deleteVisualization = async (viewId: number, visualizationId: number): Promise<void> => {
  await tempoApi().delete(
    `api/v1/loop-visualization/view/${viewId}/visualization/${visualizationId}`,
  );
};

export const VisualizationApi = {
  fetchAllVisualizations,
  fetchVisualization,
  fetchVisualizationByType,
  upsertVisualization,
  deleteVisualization,
} as const;
